<template>
    <form @submit.prevent="onSearch" class="hidden w-full shadow-lg lg:block search-wrapper">
        <div class="flex items-center gap-3">
            <div class="flex items-center w-full gap-1">
                <ifta-label class="w-full">
                    <auto-complete v-model="search" fluid placeholder="Refine Search" :suggestions="suggestions"
                        @complete="onAutoSearch" @option-select="onSelectOption" :loading="loading" :delay="100"
                        input-class="!border-none !shadow-none" option-label="name">
                        <template #option="{ option }">
                            <div class="md:w-[50vw] lg:w-[40vw] xl:w-[30vw]">
                                <div v-if="option.any">
                                    <div class="text-sm text-wrap">
                                        Searching for
                                        <strong>{{ search }}</strong>
                                    </div>
                                </div>
                                <div v-else>
                                    <div class="flex gap-2 item">
                                        <div class="flex items-center justify-center w-full overflow-hidden item-image"
                                            style="
                                                min-width: 50px;
                                                max-width: 50px;
                                                aspect-ratio: 4/3;
                                                background: whitesmoke;
                                            ">
                                            <lazy-image>
                                                <template #image="{ load }">
                                                    <img :src="(
                                                        option.images[
                                                        option
                                                            .cover_photo
                                                        ] ??
                                                        option.images[0]
                                                    ).original
                                                        " :alt="option.name" style="min-width: 120%" @load="load" />
                                                </template>
                                            </lazy-image>
                                        </div>

                                        <div class="!text-wrap text-sm">
                                            {{ option.name }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </auto-complete>

                    <label>Search</label>
                </ifta-label>
            </div>

            <div class="flex items-center gap-3">
                <p-button type="submit" rounded icon="pi pi-search" aria-label="Search" :loading="searching || loading"
                    :disabled="searching" />
                <p-button v-if="location?.explore_enabled && !explore" @click="onExplore" rounded label="Explore" text
                    severity="contrast" size="small" />
            </div>
        </div>
    </form>

    <div @click="visibleFilter = true"
        class="flex w-full items-center justify-between gap-10 px-8 text-black shadow-lg cursor-pointer lg:hidden search-wrapper">
        <div class="text-sm text-nowrap flex font-medium gap-1">Search</div>
        <div class="text-primary">
            <span class="pi pi-search"></span>
        </div>
    </div>

    <drawer position="top" v-model:visible="visibleFilter" header="Find your experience" style="height: fit-content">
        <form @submit.prevent="onSearch" class="flex flex-col justify-around h-full gap-2">
            <ifta-label class="w-full">
                <auto-complete v-model="search" fluid placeholder="Refine Search" :suggestions="suggestions"
                    @complete="onAutoSearch" @option-select="onSelectOption" :loading="loading"
                    input-class="border-none shadow-none" option-label="name" :delay="500">
                    <template #option="{ option }">
                        <div class="w-full">
                            <div class="flex gap-2 item">
                                <div class="flex items-center justify-center w-full overflow-hidden" style="
                                        min-width: 50px;
                                        max-width: 50px;
                                        aspect-ratio: 4/3;
                                        background: whitesmoke;
                                    ">
                                    <div class="item-image w-full h-full">
                                        <lazy-image>
                                            <template #image="{ load }">
                                                <img :src="(
                                                    option.images[
                                                    option.cover_photo
                                                    ] ?? option.images[0]
                                                ).original
                                                    " :alt="option.name" style="min-width: 120%" @load="load" />
                                            </template>
                                        </lazy-image>
                                    </div>

                                </div>

                                <div class="!text-wrap text-sm">
                                    {{ option.name }}
                                </div>
                            </div>
                        </div>
                    </template>
                </auto-complete>

                <label>Search</label>
            </ifta-label>

            <div class="flex items-center justify-center gap-3">
                <p-button type="submit" icon="pi pi-search" label="Search" fluid :loading="searching || loading"
                    :disabled="searching" />
                <p-button v-if="location?.explore_enabled" @click="onExplore" severity="contrast" text fluid
                    label="Explore" />
            </div>
        </form>
    </drawer>
</template>

<script setup lang="ts">
import { Button as PButton, IftaLabel, Drawer, AutoComplete } from "primevue";
import {
    AutoCompleteCompleteEvent,
    AutoCompleteOptionSelectEvent,
} from "primevue/autocomplete";
import { onMounted, ref } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import LazyImage from "@/Components/LazyImage.vue";
import { useApiProxy } from "@/Hook/useProxy";
import { Location, Tour } from "@/types";

const props = defineProps<{
    explore?: boolean;
    location?: Location;
}>();

const search = ref();

const searching = ref(false);

const visibleFilter = ref(false);

const suggestions = ref<any[]>([]);

const { request, loading } = useApiProxy();

const getRoute = function (tour: Tour) {
    const specific = usePage().props?.specific_tours;
    const params = { id: tour.id, tour_slug: tour.slug };

    let country_slug = props.location?.country_slug;
    let region_slug = props.location?.region_slug;

    if (country_slug && region_slug)
        return route(specific ? 'specific.show.tour.region' : "show.tour.region", {
            ...params,
            country: country_slug,
            region: region_slug,
        });

    if (country_slug)
        return route(specific ? 'specific.show.tour.country' : "show.tour.country", {
            ...params,
            country: country_slug,
        });

    return route(specific ? "specific.show.tour" : "show.tour", params);
};

const onExplore = function () {
    router.get(window.location.pathname);
};

const onAutoSearch = function (event: AutoCompleteCompleteEvent) {
    request(
        route("tour.search", {
            location: props.location?.slug,
            search: event.query.trim(),
        })
    )
        .then(function (res) {
            suggestions.value = res.tours;
        })
        .catch(console.error);
};

const onSearch = function () {
    router.get(
        location.href,
        { search: search.value, filter: true },
        {
            onFinish: function () {
                searching.value = false;
            },
            onBefore: function () {
                searching.value = true;
            },
        }
    );
};

const onSelectOption = function (event: AutoCompleteOptionSelectEvent) {
    if (event.value.name) {
        const tour = suggestions.value.find((t) => t.name == event.value.name);
        if (tour) {
            router.get(getRoute(tour));
            visibleFilter.value = false;
            searching.value = true;
        }
    }
};

onMounted(function () {
    search.value = usePage<any>().props.__params?.search ?? "";
});
</script>

<style scoped lang="scss">
.search-wrapper {
    padding: 8px 24px;
    border-radius: 38px;
    background: white;
}


.p-inputtext {
    box-shadow: none !important;
}

.item {
    .item-image {
        transform: scale(1);
        transition: 0.25s;
    }

    &.loading {
        cursor: wait !important;
    }

    &:hover {
        transition: 0.1s;
        cursor: pointer;

        .item-overlay {
            background-color: rgba(227, 227, 227, 0.205);
            transition: 0.25s;
        }

        .item-image {
            transform: scale(1.2);
            transition: 0.5s;
        }
    }
}
</style>
